import { usePlan } from '@/modules/auth';
import type { LoginPlan } from '@/modules/auth/types';

/**
 * Numeric plan allowances, read straight off the signed-in plan.
 *
 * The catalog (migration 0049) already carries every number the Free plan is
 * meant to cap — `limit_properties` = 10, `limit_leads_visible` = 10, with
 * every paid tier seeded NULL for "unlimited". Nothing on the dashboard read
 * them back, so they were Billing-page decoration; this module is the single
 * place that turns them into an actual cap.
 *
 * Deliberately NOT a hardcoded 10: raising the Free allowance is then a catalog
 * edit, not a code change, and a plan that seeds a different number gets it for
 * free. Anything absent (or a plan that hasn't hydrated) is treated as
 * unlimited — gating must never block on missing data.
 *
 * Feature-shaped entitlements (Sending Identity, Lead Routing) are booleans,
 * not counts, and live in `plan-gates` instead. Counts the API already reports
 * on its own responses (the lead window) belong there, not here.
 */

/** Catalog tier code shared by every no-card plan (`solo-free`, `team-free`, …). */
export const FREE_TIER_CODE = 'free';

export function isFreePlan(plan: LoginPlan | null | undefined): boolean {
    return plan?.tier_code === FREE_TIER_CODE;
}

/**
 * Headroom left against a plan allowance. A null limit is unlimited and yields
 * Infinity, so callers can write `remaining > 0` without special-casing the
 * paid tiers.
 */
export function remainingOf(limit: number | null | undefined, used: number): number {
    if (limit == null) return Infinity;
    return Math.max(0, limit - used);
}

export interface PlanLimits {
    /** True on the Free tier — the only tier that currently caps anything. */
    isFree:     boolean;
    /** Properties the workspace may hold; null = unlimited. */
    properties: number | null;
}

/**
 * `limit_leads_visible` is deliberately absent: the lead window is enforced in
 * SQL and the API reports the resulting `visible_limit` / `locked_count` on the
 * list response itself. Reading the plan for it here would be a second source
 * of truth for one number, free to drift from the one enforcement uses.
 */
export function usePlanLimits(): PlanLimits {
    const plan = usePlan();
    return {
        isFree:     isFreePlan(plan),
        properties: plan?.limits.properties ?? null,
    };
}
